import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import CheckoutForm from './components/CheckoutForm';
import { auth } from './lib/firebase'; // Import auth to get current user
import usePageTitle from './utils/usePageTitle';
import { getUserCountry, findPlanByPriceId } from './utils/countryPricing';

// Helper to format phone numbers nicely
function formatPhoneNumber(phoneNumber) {
  if (!phoneNumber) return '';
  
  // For standard E.164 format like +14159998941
  if (phoneNumber.startsWith('+')) {
    // Format US numbers with parentheses
    if (phoneNumber.startsWith('+1') && phoneNumber.length === 12) {
      return `+1(${phoneNumber.substring(2, 5)})${phoneNumber.substring(5, 8)}-${phoneNumber.substring(8, 12)}`;
    }
    // Format other international numbers with parentheses around country code
    const countryCode = phoneNumber.substring(1).match(/^\d+/)[0];
    const restOfNumber = phoneNumber.substring(1 + countryCode.length);
    if (restOfNumber.length > 6) {
      return `+${countryCode}(${restOfNumber.substring(0, 3)})${restOfNumber.substring(3, 6)}-${restOfNumber.substring(6)}`;
    }
    return phoneNumber; // Fallback for unusual formats
  }
  
  return phoneNumber;
}

// Make sure to call loadStripe outside of a component's render to avoid recreating the Stripe object on every render
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// $19.99 monthly consumer plan — runs the authorization-hold + 3-day free-trial flow.
// Other `period: 'Month'` plans (toll-free, forwarding, the $9.99 monthly) do NOT get a real
// trial from the API, so trial copy on this page is only shown for non-monthly plans OR this plan.
const MONTHLY_TRIAL_PRICE_ID = 'price_1SUHVAK5A174OlbcGyZSQjoQ';

// True when the selected plan should render free-trial copy on the checkout page
function planShowsTrial(plan, isAuthorizationFlow = false) {
  if (!plan?.hasTrial) return false;
  if (plan.period !== 'Month') return true;
  return plan.id === MONTHLY_TRIAL_PRICE_ID || isAuthorizationFlow;
}

export default function Checkout() {
  usePageTitle('Checkout');

  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [stripeCustomerId, setStripeCustomerId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState('');
  const [apiResponse, setApiResponse] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [uid, setUid] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [userCountry, setUserCountry] = useState('US');
  const [countryLoaded, setCountryLoaded] = useState(false);
  const [isAuthorizationFlow, setIsAuthorizationFlow] = useState(false);
  const [authPaymentIntentId, setAuthPaymentIntentId] = useState('');

  // Check if viewport is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Set a flag in sessionStorage to indicate the user visited the checkout page
  useEffect(() => {
    sessionStorage.setItem('cameFromCheckout', 'true');
    
    // Reset loading state when component mounts (fixes back button issue)
    setIsSubmitting(false);
    
    // Get selected plan from sessionStorage first (current session takes priority)
    const planData = sessionStorage.getItem('selectedPlan');
    if (planData) {
      try {
        const plan = JSON.parse(planData);
        setSelectedPlan(plan);
        return; // Exit early, session storage takes priority
      } catch (error) {
        console.error('Error parsing selected plan:', error);
      }
    }
    
    // Check for priceId URL parameter as fallback (for abandoned cart emails)
    const urlParams = new URLSearchParams(window.location.search);
    const urlPriceId = urlParams.get('priceId');
    
    if (urlPriceId) {
      // If priceId is provided in URL, use it to find the plan
      const planFromPriceId = findPlanByPriceId(urlPriceId);
      if (planFromPriceId) {
        setSelectedPlan(planFromPriceId);
        return; // Exit early
      } else {
        console.warn('Plan not found for priceId from URL:', urlPriceId);
      }
    }
    
    // Final fallback to default trial plan (monthly, 3-day trial via authorization hold)
    setSelectedPlan({
      id: MONTHLY_TRIAL_PRICE_ID,
      name: '1 Line + Messaging',
      price: '$19.99',
      hasTrial: true,
      trialDays: 3,
      lines: 1,
      period: 'Month',
      isMessaging: true
    });
  }, []);

  useEffect(() => {
    // Listen for auth state changes to get the current user
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUid(user.uid);
        setCustomerEmail(user.email);
        setCustomerPhone(user.phoneNumber);
        
        // Fetch user's country
        try {
          const country = await getUserCountry(user.uid);
          setUserCountry(country);
        } catch (error) {
          console.error('Error loading user country in checkout:', error);
          setUserCountry('US');
        } finally {
          setCountryLoaded(true);
        }

        // Check for prefetched checkout data from /welcome
        const prefetchRaw = sessionStorage.getItem('checkoutPrefetch');
        if (prefetchRaw && selectedPlan) {
          try {
            const prefetch = JSON.parse(prefetchRaw);
            const age = Date.now() - (prefetch.prefetchedAt || 0);
            // Use prefetched data if it's less than 5 minutes old
            if (prefetch.clientSecret && prefetch.customerId && age < 5 * 60 * 1000) {
              setStripeCustomerId(prefetch.customerId);
              setClientSecret(prefetch.clientSecret);
              if (prefetch.isAuthorizationFlow) {
                setIsAuthorizationFlow(true);
                setAuthPaymentIntentId(prefetch.paymentIntentId);
              }
              sessionStorage.removeItem('checkoutPrefetch');
              return;
            }
          } catch (e) {
            console.error('Error reading prefetched checkout data:', e);
          }
          sessionStorage.removeItem('checkoutPrefetch');
        }
        
        // Fallback: create customer and subscription via API call
        if (selectedPlan) {
          createSubscriptionCheckout(user.uid, user.email, user.phoneNumber);
        }
      } else {
        setError('You must be logged in to access this page');
        setCountryLoaded(true);
      }
    });

    return () => unsubscribe();
  }, [selectedPlan]);

  // Update selected plan when country is loaded (for fallback plans)
  useEffect(() => {
    if (countryLoaded && selectedPlan && selectedPlan.id === 'price_1SG8DCK5A174Olbc6IiZ6685' && userCountry === 'CA') {
      // Update US fallback plan to CA equivalent - need to find CA weekly messaging plan
      // For now, keep the US weekly plan as there might not be a CA equivalent
    }
  }, [countryLoaded, userCountry, selectedPlan]);

  // Fire TriplePixel AddToCart event when component mounts
  useEffect(() => {
    // Check if TriplePixel is available
    if (window.TriplePixel) {
      window.TriplePixel('AddToCart', {item: '4428522618989', q: 10, v: '9898162258244'});
    }
  }, []);

  const createSubscriptionCheckout = (userId, email, phoneNumber) => {
    // Use optimized single API call for customer + subscription creation
    const requestBody = {
      uid: userId,
      priceId: selectedPlan.id
    };
    
    // Add email if available, otherwise add phone number
    if (email) {
      requestBody.email = email;
    } else if (phoneNumber) {
      requestBody.phoneNumber = phoneNumber;
    }
    
    fetch('/api/create-subscription-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    })
      .then(res => {
        // Handle the case where user already has active subscription
        if (!res.ok) {
          return res.json().then(errorData => {
            if (errorData.redirectTo === '/overview' && errorData.error === 'User already has active subscription') {
              window.location.href = '/overview';
              return;
            }
            throw new Error(errorData.error || `API returned status ${res.status}`);
          });
        }
        return res.json();
      })
      .then(data => {
        if (!data) return; // Handle case where we redirected
        
        if (data.clientSecret && data.customerId) {
          setStripeCustomerId(data.customerId);
          setClientSecret(data.clientSecret);
          if (data.isAuthorizationFlow) {
            setIsAuthorizationFlow(true);
            setAuthPaymentIntentId(data.paymentIntentId);
          }
        } else {
          throw new Error('Missing client secret or customer ID from optimized checkout');
        }
      })
      .catch(err => {
        console.error('Error creating optimized subscription checkout:', err);
        setError(`Failed to initialize payment: ${err.message}`);
      });
  };

  const fetchSetupIntent = (userId, email, customerId) => {
    // Create a setup intent to get the client secret (for trial plans)
    fetch('/api/create-setup-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        email,
        customerId // Pass customer ID to setup intent
      })
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`API returned status ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        setApiResponse(data);
        
        if (!data.clientSecret) {
          throw new Error('API response missing clientSecret');
        }
        
        setClientSecret(data.clientSecret);
        // Don't set loading to false since we start with loading: false
      })
      .catch((err) => {
        console.error('Error fetching client secret:', err);
        setError(`Failed to initialize payment: ${err.message}`);
        // Don't block UI for setup intent errors
      });
  };

  const appearance = {
    theme: 'night',
    variables: {
      colorPrimary: '#6366f1', // Indigo-500
      colorBackground: '#1f2937', // Gray-800
      colorText: '#f9fafb', // Gray-50
      colorDanger: '#ef4444', // Red-500
      fontFamily: 'system-ui, -apple-system, sans-serif',
      spacingUnit: '4px',
      borderRadius: '8px',
    },
  };

  // Create options for Stripe Elements - use client secret from incomplete subscription
  const options = clientSecret ? {
    clientSecret,
    appearance,
  } : {
    // Fallback options while client secret is loading
    mode: 'subscription',
    amount: selectedPlan ? Math.round(parseFloat(selectedPlan.price.replace(/^CA\$|\$/, '')) * 100) : 0,
    currency: userCountry === 'CA' ? 'cad' : 'usd',
    appearance,
  };

  // Function to handle payment status updates from CheckoutForm
  const handlePaymentStatusChange = (status) => {
    if (status === 'error') {
      setIsSubmitting(false); // Reset sticky button on error
    }
  };

  // Function to trigger form submission from the sticky button
  const handleStickyButtonClick = () => {
    // Only proceed if not already submitting
    if (isSubmitting) return;
    
    // Find the submit button in the CheckoutForm and click it
    const submitButton = document.getElementById('submit');
    if (submitButton) {
      setIsSubmitting(true);
      submitButton.click();
      
      // Add a fallback to reset the button state if something goes wrong
      // This ensures the button doesn't get stuck in the loading state
      setTimeout(() => {
        setIsSubmitting(false);
      }, 10000); // Reset after 10 seconds as a safety measure
    }
  };

  // Helper to get period display text
  const getPeriodText = () => {
    if (!selectedPlan) return '';
    if (selectedPlan.period === 'Month') return 'monthly';
    if (selectedPlan.period === 'Weekly') return 'weekly';
    if (selectedPlan.period === 'Annual') return 'annually';
    if (selectedPlan.period === '3-Days') return 'every 3 days';
    if (selectedPlan.period === '3-months') return 'every 3 months';
    return selectedPlan.period.toLowerCase();
  };

  const showsTrial = planShowsTrial(selectedPlan, isAuthorizationFlow);

  // Show loading state while country is being detected
  if (!countryLoaded) {
    return (
      <div 
        className='min-h-screen text-white relative flex items-center justify-center'
        style={{
          background: 'linear-gradient(to bottom right, #0d0520, #1a0b2e, #0a0615, #050208)'
        }}
      >
        <div className="flex items-center space-x-3">
          <svg className="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-white/80">Loading checkout...</span>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen text-white flex items-center justify-center"
      style={{
        background: 'linear-gradient(to bottom right, #0d0520, #1a0b2e, #0a0615, #050208)'
      }}
    >
      {/* User email or phone display in upper right - Desktop only */}
      {(customerEmail || customerPhone) && !isMobile && (
        <div className="fixed top-4 right-4 sm:top-6 sm:right-6 bg-black/20 backdrop-blur-2xl rounded-2xl px-4 py-3 border border-white/10 shadow-2xl z-50"
             style={{
               boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
             }}>
          <div className="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="text-sm text-white/80 font-medium truncate max-w-[200px]">
              {customerPhone ? formatPhoneNumber(customerPhone) : customerEmail}
            </span>
          </div>
        </div>
      )}
      
      <div className={`w-full max-w-lg mx-auto ${isMobile ? 'p-4 pb-32' : 'p-8 pb-24'}`}>

        {/* Main card */}
        <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
             style={{
               boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 16px 64px rgba(0,0,0,0.4)'
             }}>
          
          {/* Header with plan info */}
          {selectedPlan && (
            <div className={`${
              showsTrial
                ? (selectedPlan.period === 'Weekly' || selectedPlan.period === 'Month' || selectedPlan.period === '3-Days' || selectedPlan.period === '3-months')
                  ? 'bg-gradient-to-r from-purple-500/20 to-blue-500/20'
                  : selectedPlan.period === 'Annual'
                  ? 'bg-gradient-to-r from-purple-500/20 to-blue-500/20'
                  : 'bg-gradient-to-r from-green-500/20 to-emerald-500/20'
                : 'bg-gradient-to-r from-purple-500/20 to-blue-500/20'
            } border-b border-white/10 p-6 text-center`}>
              {showsTrial ? (
                <div>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mb-3 ${
                    (selectedPlan.period === 'Weekly' || selectedPlan.period === 'Month' || selectedPlan.period === '3-Days' || selectedPlan.period === '3-months')
                      ? 'bg-purple-500/20 text-purple-300 border border-purple-400/30'
                      : selectedPlan.period === 'Annual'
                      ? 'bg-purple-500/20 text-purple-300 border border-purple-400/30'
                      : 'bg-green-500/20 text-green-300 border border-green-400/30'
                  }`}>
                                         ✨ {selectedPlan.trialDays} Days Free Trial
                  </span>
                  <h2 className="text-xl font-light text-white mb-1">{selectedPlan.name}</h2>
                  <p className="text-white/70 text-sm">
                    {selectedPlan.isMessaging 
                      ? selectedPlan.period === 'Annual' && selectedPlan.originalPrice
                        ? `Then ${selectedPlan.price}/mo when paid annually (${selectedPlan.originalPrice} total)`
                        : selectedPlan.period === '3-Days'
                          ? `${selectedPlan.price} every 3-days pass`
                          : selectedPlan.period === '3-months'
                            ? `$1.99/day billed every 3-months`
                            : `Then ${selectedPlan.price}/${selectedPlan.period === 'Weekly' ? 'week' : 'mo'} billed ${selectedPlan.period === 'Weekly' ? 'weekly' : 'monthly'}`
                      : `Then ${selectedPlan.price}/${getPeriodText()}`
                    }
                  </p>
                </div>
              ) : (
                <div>
                  <h2 className="text-xl font-light text-white mb-1">{selectedPlan.name}</h2>
                  <p className="text-white/70 text-sm">
                    {selectedPlan.isMessaging 
                      ? selectedPlan.period === 'Annual' && selectedPlan.originalPrice
                        ? `${selectedPlan.price}/mo when paid annually (${selectedPlan.originalPrice} total)`
                        : selectedPlan.period === '3-Days'
                          ? `${selectedPlan.price} every 3 days`
                          : selectedPlan.period === '3-months'
                            ? `$1.99/day billed every 3-months`
                            : selectedPlan.period === 'Weekly'
                              ? `${selectedPlan.price}/week billed weekly`
                              : `${selectedPlan.price}/mo billed monthly`
                      : `${selectedPlan.price} ${getPeriodText()}`
                    }
                  </p>
                  {selectedPlan.isMessaging && (
                    <p className="text-blue-300 text-xs mt-1">Includes messaging platform</p>
                  )}
                </div>
              )}
          </div>
          )}

          <div className={`${isMobile ? 'p-6' : 'p-8'}`}>
            {error ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-medium text-white mb-2">Something went wrong</h3>
                <p className="text-red-400 mb-4">{error}</p>
                {apiResponse && (
                  <details className="text-left">
                    <summary className="text-white/60 cursor-pointer mb-2">Technical Details</summary>
                    <pre className="text-xs text-white/50 bg-white/5 p-3 rounded-lg overflow-auto">
                      {JSON.stringify(apiResponse, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            ) : selectedPlan ? (
              <div>
                {/* Payment method section header */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-white mb-2">
                    Payment details
                  </h3>
                  <p className="text-sm text-white/60">
                    {showsTrial
                      ? `Start your ${selectedPlan.trialDays}-day free trial. You won't be charged during the trial period.`
                      : selectedPlan?.isMessaging
                        ? selectedPlan?.period === 'Annual' && selectedPlan?.originalPrice
                          ? `You'll be charged ${selectedPlan.originalPrice} annually (${selectedPlan.price}/mo).`
                          : selectedPlan?.period === '3-Days'
                            ? `You'll be charged ${selectedPlan?.price} every 3 days.`
                            : selectedPlan?.period === '3-months'
                              ? `You'll be charged $1.99/day billed every 3-months.`
                              : selectedPlan?.period === 'Weekly'
                                ? `You'll be charged ${selectedPlan?.price}/week billed weekly.`
                                : `You'll be charged ${selectedPlan?.price}/mo billed monthly.`
                        : `You'll be charged ${selectedPlan?.price} ${getPeriodText()}.`
                    }
                  </p>
                </div>

                {/* Show Elements when ready or form placeholder */}
                {clientSecret ? (
              <Elements stripe={stripePromise} options={options}>
                <CheckoutForm 
                  uid={uid} 
                  customerEmail={customerEmail} 
                  stripeCustomerId={stripeCustomerId} 
                  setParentSubmitting={setIsSubmitting}
                  isParentSubmitting={isSubmitting}
                  onStatusChange={handlePaymentStatusChange}
                      selectedPlan={selectedPlan}
                      isAuthorizationFlow={isAuthorizationFlow}
                      authPaymentIntentId={authPaymentIntentId}
                />
              </Elements>
                ) : (
                  /* Form placeholder - no loading spinner */
                  <div className="space-y-4">
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="space-y-3">
                        <div className="h-10 bg-white/5 rounded animate-pulse"></div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="h-10 bg-white/5 rounded animate-pulse"></div>
                          <div className="h-10 bg-white/5 rounded animate-pulse"></div>
                        </div>
                        <div className="h-10 bg-white/5 rounded animate-pulse"></div>
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-white/50 text-sm">Setting up secure payment...</p>
                      <div className="flex items-center justify-center mt-2">
                        <div className="w-4 h-4 border-2 border-white/20 border-t-white/60 rounded-full animate-spin"></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // No plan selected - show plan selection guidance
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-medium text-white mb-2">No plan selected</h3>
                <p className="text-white/60 mb-4">Please select a plan to continue</p>
                <a 
                  href="/welcome"
                  className="inline-flex items-center px-6 py-3 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-400/30 text-white rounded-xl transition-all duration-300"
                >
                  Choose Plan
                </a>
              </div>
            )}
          </div>
        </div>

      </div>
      
      {/* Sticky button at the bottom */}
      {selectedPlan && !error && clientSecret && (
        <div className="fixed bottom-0 left-0 right-0 bg-black/30 backdrop-blur-2xl border-t border-white/10 shadow-2xl z-50 py-4 px-4 md:px-8"
             style={{
               boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 -8px 32px rgba(0,0,0,0.4)'
             }}>
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            
            {/* Plan summary - visible on desktop */}
            {!isMobile && selectedPlan && (
              <div className="flex items-center space-x-6">
                <div>
                  <div className="flex items-center">
                    <span className="text-white/70 mr-2">{selectedPlan.name}:</span>
                    {showsTrial ? (
                      <>
                        <span className="line-through text-white/50 mr-2">{selectedPlan.price}</span>
                        <span className="text-green-400 font-medium">$0</span>
                      </>
                    ) : (
                      <span className="text-white font-medium">{selectedPlan.price}</span>
                    )}
                  </div>
                  <div className="text-white/60 text-sm">
                    {showsTrial
                      ? `Free for ${selectedPlan.trialDays} days` 
                      : selectedPlan.isMessaging
                        ? selectedPlan.period === '3-Days'
                          ? 'Billed every 3 days'
                          : selectedPlan.period === '3-months'
                            ? 'Billed every 3 months'
                            : selectedPlan.period === 'Weekly'
                              ? 'Billed weekly'
                              : 'Billed monthly'
                        : `Billed ${getPeriodText()}`
                    }
                  </div>
                </div>
              </div>
            )}

            <button 
              onClick={handleStickyButtonClick}
              disabled={isSubmitting}
              className={`w-full sm:w-auto ${
                selectedPlan?.hasTrial && selectedPlan?.period === 'Monthly'
                  ? 'bg-green-500/20 hover:bg-green-500/30 border-green-400/30' 
                  : 'bg-purple-500/20 hover:bg-purple-500/30 border-purple-400/30'
              } backdrop-blur-sm border px-8 py-4 rounded-xl text-white font-medium transition-all duration-300 hover:scale-105 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
              style={{
                boxShadow: `inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(${selectedPlan?.hasTrial && selectedPlan?.period === 'Monthly' ? '34, 197, 94' : '147, 51, 234'}, 0.2)`
              }}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <span>Processing...</span>
                  <svg className="animate-spin ml-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              ) : (
                showsTrial ? 'Start Free Trial' : 'Complete Purchase'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
} 